import React from 'react';
import {
  forPlatform,
  screenFor,
  aliasTargetPath,
  aliasTargetIsPublic,
  PLATFORM_WEB,
} from '@tenant/core/routes';
import NavigateHomeFeature from '../features/navigate/navigate-home-feature';
import NavigateLoginFeature from '../features/navigate/navigate-login-feature';
import NavigatePropertiesFeature from '../features/navigate/navigate-properties-feature';
import NavigateAliasFeature from '../features/navigate/navigate-alias-feature';
import NavigateAliasLoginFeature from '../features/navigate/navigate-alias-login-feature';
import screens from './screens';
import icons from './icons';

/**
 * Joins the shared route manifest to the web app's screen and icon registries.
 *
 * The manifest states intent (`screen`, `redirectTo`, `aliasOf`); this module
 * owns the web mechanism for each. Output keeps the exact shape the existing
 * consumers destructure — `{slug, path, title, label, Component, Icon,
 * showInSideNav, ...}` — so `features/routes/*`, `use-route-title`,
 * `side-nav-feature` and `footer-nav-feature` needed no changes.
 */

/** Redirect targets. `home` and `login` preserve a `back_url`. */
const REDIRECTS = {
  home: NavigateHomeFeature,
  login: NavigateLoginFeature,
  properties: NavigatePropertiesFeature,
};

/**
 * Resolves a single manifest entry to a renderable component.
 *
 * @param {object} route - Manifest entry.
 * @param {boolean} authenticated - Whether the visitor is logged in.
 * @returns {React.ComponentType|null}
 */
const componentFor = (route, authenticated) => {
  if (route.aliasOf) {
    // The target path comes from the canonical route set, not from the
    // manifest this alias sits in: the logged-out manifest has no `viewings`
    // entry, only its `unauth_viewings` bounce. Logged in, an alias resolves
    // to the canonical route. Logged out, it resolves to login with the
    // canonical path as back_url, unless the target is a public screen: that
    // needs no login, so it resolves directly for everyone. Public manifests
    // are always resolved as logged out, so without this check a public alias
    // would send anonymous visitors to login.
    const to = aliasTargetPath(route);
    const Alias =
      authenticated || aliasTargetIsPublic(route)
        ? NavigateAliasFeature
        : NavigateAliasLoginFeature;
    const AliasRoute = () => <Alias to={to} />;
    // eslint-disable-next-line fp/no-mutation
    AliasRoute.displayName = `AliasRoute(${route.slug}->${route.aliasOf})`;
    return AliasRoute;
  }

  if (route.redirectTo) {
    return REDIRECTS[route.redirectTo] || null;
  }

  return screens[screenFor(route)] || null;
};

/**
 * Maps a manifest to web route entries.
 *
 * @param {object[]} manifest - Output of a `*Routes()` manifest function.
 * @param {object} options
 * @param {boolean} options.authenticated
 * @returns {object[]} Route entries with `Component` and `Icon` resolved.
 */
const resolveWebRoutes = (manifest, {authenticated = false} = {}) => {
  const resolved = manifest.filter(forPlatform(PLATFORM_WEB)).map(route => ({
    ...route,
    Icon: route.icon ? icons[route.icon] : undefined,
    Component: componentFor(route, authenticated),
  }));

  if (process.env.NODE_ENV !== 'production') {
    // A manifest entry with nothing to render is silently dropped below, which
    // looks identical to a deliberately native-only route. Say so out loud.
    const orphans = resolved.filter(route => !route.Component);
    if (orphans.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[routes] no web screen registered for: ${orphans
          .map(route => `${route.slug} (screen: ${screenFor(route)})`)
          .join(', ')}`
      );
    }
  }

  return resolved.filter(route => route.Component);
};

export default resolveWebRoutes;
